import { useEffect, useState } from 'react';
import { boardsApi } from '../api/endpoints/boardsApi';

export interface BoardMember {
  id: number;
  fullName: string;
  userName: string;
  email: string;
  role: string;
}

/**
 * Fetches everyone who's actually assignable on a board's tasks: direct
 * project members plus members of any team assigned to the project (via
 * GET /boards/:id/access). Shared by any UI that needs to assign a task
 * to someone - keeps "who shows up in the picker" in sync with the
 * backend's own assignee-membership check (TaskService.assertValidAssignee),
 * so it doesn't offer someone the backend will then reject.
 */
export const useBoardMembers = (boardId?: string) => {
  const [members, setMembers] = useState<BoardMember[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!boardId) {
      setMembers([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    boardsApi
      .getBoardAccess(boardId)
      .then((access) => {
        if (cancelled) return;

        const byId = new Map<number, BoardMember>();
        (access.members || []).forEach((m: BoardMember) => byId.set(m.id, m));
        (access.teams || []).forEach((team) => {
          team.members.forEach((m: BoardMember) => {
            if (!byId.has(m.id)) byId.set(m.id, m);
          });
        });

        setMembers(Array.from(byId.values()));
      })
      .catch((error) => {
        console.error('Failed to fetch board members:', error);
        if (!cancelled) setMembers([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [boardId]);

  return { members, loading };
};
